import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Loader2, Plus, SquarePen, Trash2, Briefcase, Upload, FileText, Eye, X, Receipt, Building2, Calendar, TrendingUp, DollarSign, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkspace } from '../../context/WorkspaceContext';
import { crmAPI, formatIDR, PROGRESS_OPTIONS, buildPaymentOptions, getProgressLabel, getPaymentLabel } from '../../lib/crm';
import api from '../../lib/api';

function initialForm() {
    return {
        client_id: '',
        name: '', value: 0, description: '',
        year: new Date().getFullYear(),
        terms_of_payment: 1,
        start_date: new Date().toISOString().slice(0, 10),
        due_date: '',
        progress_status: 'not_started',
        payment_status: 'belum_bayar',
        po_file: null, bast_file: null, invoice_file: null,
    };
}

export default function CrmProjectsPage() {
    const { currentWorkspace } = useWorkspace();
    const [rows, setRows] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(initialForm());
    const [saving, setSaving] = useState(false);
    const [clientFilter, setClientFilter] = useState('__all__');
    const [detail, setDetail] = useState(null);

    const load = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        try {
            setLoading(true);
            const [pres, cres] = await Promise.all([
                crmAPI.listProjects(currentWorkspace.id),
                crmAPI.listClients(currentWorkspace.id),
            ]);
            setRows(pres.data || []);
            setClients(cres.data || []);
        } catch (e) {
            toast.error('Gagal memuat projects');
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace?.id]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setEditing(null); setForm(initialForm()); setFormOpen(true); };
    const openEdit = (row) => {
        setEditing(row);
        setForm({
            client_id: row.client_id,
            name: row.name, value: row.value, description: row.description || '',
            year: row.year, terms_of_payment: row.terms_of_payment,
            start_date: row.start_date, due_date: row.due_date,
            progress_status: row.progress_status, payment_status: row.payment_status,
            po_file: row.po_file, bast_file: row.bast_file, invoice_file: row.invoice_file,
        });
        setFormOpen(true);
    };

    const save = async () => {
        if (!form.client_id) return toast.error('Pilih Client');
        if (!form.name?.trim()) return toast.error('Nama Project wajib');
        if (!form.due_date) return toast.error('Due date wajib');
        try {
            setSaving(true);
            const payload = {
                ...form,
                value: Number(form.value) || 0,
                year: parseInt(form.year, 10),
                terms_of_payment: parseInt(form.terms_of_payment, 10) || 1,
            };
            if (editing) {
                await crmAPI.updateProject(editing.id, payload);
                toast.success('Project diperbarui');
            } else {
                await crmAPI.createProject({ workspace_id: currentWorkspace.id, ...payload });
                toast.success('Project dibuat');
            }
            setFormOpen(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Hapus project "${row.name}"?`)) return;
        try { await crmAPI.deleteProject(row.id); toast.success('Dihapus'); await load(); }
        catch (e) { toast.error('Gagal'); }
    };

    const filteredRows = clientFilter && clientFilter !== '__all__'
        ? rows.filter((r) => r.client_id === clientFilter)
        : rows;

    return (
        <Layout title="Project" subtitle="Paket pekerjaan untuk klien">
            <div className="flex justify-between items-end mb-6 gap-3 flex-wrap">
                <div className="w-72">
                    <Label className="text-xs text-brand-text-muted mb-1.5 block">Filter Client</Label>
                    <Select value={clientFilter} onValueChange={setClientFilter}>
                        <SelectTrigger data-testid="project-client-filter">
                            <SelectValue placeholder="Semua Client..." />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Semua Client</SelectItem>
                            {clients.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.partnership?.company_name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <Button onClick={openCreate} data-testid="project-create-btn">
                    <Plus className="w-4 h-4 mr-2" /> Tambah Project
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
            ) : filteredRows.length === 0 ? (
                <div className="text-center py-20 text-brand-text-muted">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>Belum ada Project</p>
                </div>
            ) : (
                <div className="bg-white border border-brand-border rounded-lg overflow-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Perusahaan</TableHead>
                                <TableHead>Nama Project</TableHead>
                                <TableHead>Nilai</TableHead>
                                <TableHead>Terbayar</TableHead>
                                <TableHead>Start</TableHead>
                                <TableHead>Due</TableHead>
                                <TableHead>Progress</TableHead>
                                <TableHead>Payment</TableHead>
                                <TableHead className="text-right">Aksi</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredRows.map((r) => (
                                <TableRow key={r.id} data-testid={`project-row-${r.id}`}>
                                    <TableCell className="font-medium">{r.partnership?.company_name || '-'}</TableCell>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell>{formatIDR(r.value)}</TableCell>
                                    <TableCell>{formatIDR(r.total_paid || 0)}</TableCell>
                                    <TableCell>{r.start_date}</TableCell>
                                    <TableCell>{r.due_date}</TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="text-xs">{getProgressLabel(r.progress_status)}</Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="text-xs">{getPaymentLabel(r.payment_status, r.terms_of_payment)}</Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                            <Button variant="ghost" size="icon" onClick={async () => {
                                                const res = await crmAPI.getProject(r.id);
                                                setDetail(res.data);
                                            }} data-testid={`project-detail-${r.id}`}><Eye className="w-4 h-4" /></Button>
                                            <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`project-edit-${r.id}`}><SquarePen className="w-4 h-4" /></Button>
                                            <Button variant="ghost" size="icon" onClick={() => remove(r)} data-testid={`project-delete-${r.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-form">
                    <DialogHeader><DialogTitle>{editing ? 'Edit Project' : 'Tambah Project'}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-sm font-medium">Client *</Label>
                            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })} disabled={!!editing}>
                                <SelectTrigger data-testid="project-client-select"><SelectValue placeholder="Pilih client..." /></SelectTrigger>
                                <SelectContent>
                                    {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.partnership?.company_name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Nama Project *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="pj-name" />
                            <CurrencyField label="Nilai Project (IDR)" value={form.value} onChange={(v) => setForm({ ...form, value: v })} testid="pj-value" />
                            <Field label="Tahun *" type="number" value={form.year} onChange={(v) => setForm({ ...form, year: v })} testid="pj-year" />
                            <Field label="Jumlah Termin *" type="number" min={1} value={form.terms_of_payment} onChange={(v) => setForm({ ...form, terms_of_payment: v })} testid="pj-terms" />
                            <Field label="Start Date *" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="pj-start" />
                            <Field label="Due Date *" type="date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testid="pj-due" />
                        </div>
                        <div>
                            <Label className="text-sm font-medium">Deskripsi</Label>
                            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pj-desc" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFormOpen(false)}>Batal</Button>
                        <Button onClick={save} disabled={saving} data-testid="project-save-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-0 gap-0" data-testid="project-detail">
                    {detail && <ProjectDetail project={detail} onReload={async () => {
                        const res = await crmAPI.getProject(detail.id);
                        setDetail(res.data);
                        await load();
                    }} />}
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

function Field({ label, value, onChange, type = 'text', testid, min }) {
    return (
        <div>
            <Label className="text-sm font-medium">{label}</Label>
            <Input type={type} min={min} value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        </div>
    );
}

function CurrencyField({ label, value, onChange, testid }) {
    const formatDisplay = (num) => {
        if (num == null || num === '') return '';
        return 'IDR ' + Number(num).toLocaleString('id-ID');
    };
    const handleChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        const num = raw === '' ? 0 : parseInt(raw, 10);
        onChange(num);
    };
    return (
        <div>
            <Label className="text-sm font-medium">{label}</Label>
            <Input type="text" value={formatDisplay(value)} onChange={handleChange} placeholder="IDR 0" data-testid={testid} />
        </div>
    );
}

// =========================== PROJECT DETAIL (UI/UX Improved) ===========================

function ProjectDetail({ project, onReload }) {
    const p = project;
    const partnership = p.partnership || {};
    const paid = p.total_paid || 0;
    const value = p.value || 0;
    const remaining = Math.max(0, value - paid);
    const percentPaid = value > 0 ? Math.min(100, Math.round((paid / value) * 100)) : 0;

    return (
        <div>
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-brand-border/60">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3 text-xl">
                        <div className="w-10 h-10 bg-brand-accent-soft rounded-xl flex items-center justify-center flex-shrink-0">
                            <Briefcase className="w-5 h-5 text-brand-primary" />
                        </div>
                        <div className="min-w-0">
                            <div className="truncate">{p.name}</div>
                            <div className="text-sm font-normal text-brand-text-muted mt-0.5">{partnership.company_name}</div>
                        </div>
                    </DialogTitle>
                </DialogHeader>
            </div>

            <div className="p-6 space-y-6">
                {/* Stats Cards Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard icon={DollarSign} label="Nilai Project" value={formatIDR(value)} />
                    <StatCard icon={Receipt} label="Terbayar" value={formatIDR(paid)} accent />
                    <StatCard icon={TrendingUp} label="Sisa" value={formatIDR(remaining)} warning={remaining > 0 && percentPaid < 100} />
                    <StatCard icon={Calendar} label="Due Date" value={p.due_date || '-'} />
                </div>

                {/* Payment Progress Bar */}
                {value > 0 && (
                    <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-brand-text-muted">Progress Pembayaran</span>
                            <span className="font-semibold">{percentPaid}%</span>
                        </div>
                        <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-emerald-500 rounded-full transition-all duration-500 ease-out"
                                style={{ width: `${percentPaid}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Column 1: Project Info */}
                    <div className="space-y-4">
                        <SectionTitle icon={Building2} title="Informasi Project" />
                        <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-brand-border/40">
                            <InfoRow label="Client" value={partnership.company_name} />
                            <InfoRow label="PIC" value={`${partnership.pic_name || '-'} — ${partnership.pic_title || ''}`} />
                            <InfoRow label="Email PIC" value={partnership.pic_email} />
                            <InfoRow label="Phone PIC" value={partnership.pic_phone} />
                            <InfoRow label="Termin Pembayaran" value={`${p.terms_of_payment}x termin`} />
                            <InfoRow label="Start Date" value={p.start_date} />
                            <InfoRow label="Due Date" value={p.due_date} />
                            {p.description && (
                                <div className="pt-2 border-t border-brand-border/30">
                                    <div className="text-xs text-brand-text-muted mb-1">Deskripsi</div>
                                    <div className="text-sm text-slate-700 leading-relaxed">{p.description}</div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Column 2: Status & Documents */}
                    <div className="space-y-4">
                        <SectionTitle icon={FileCheck} title="Status &amp; Dokumen" />
                        <div className="bg-slate-50 rounded-xl p-4 space-y-4 border border-brand-border/40">
                            <StatusControl
                                label="Progress Status"
                                value={p.progress_status}
                                options={PROGRESS_OPTIONS}
                                getLabel={getProgressLabel}
                                onChange={(v) => updateProjectStatus(p.id, { progress_status: v }, onReload)}
                            />

                            <div className="space-y-3 pt-2">
                                <DocUploadRow
                                    label="PO (Purchase Order)"
                                    url={p.po_file}
                                    kind="po"
                                    projectId={p.id}
                                    onReload={onReload}
                                />
                                <DocUploadRow
                                    label="BAST"
                                    url={p.bast_file}
                                    kind="bast"
                                    projectId={p.id}
                                    onReload={onReload}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payment History - Full Width Section */}
                <PaymentSection project={p} onReload={onReload} />
            </div>
        </div>
    );
}

function SectionTitle({ icon: Icon, title }) {
    return (
        <div className="flex items-center gap-2 text-sm font-semibold text-brand-text-muted">
            <Icon className="w-4 h-4" />
            {title}
        </div>
    );
}

function StatCard({ icon: Icon, label, value, accent, warning }) {
    return (
        <div className={`rounded-xl p-4 border ${accent ? 'bg-emerald-50 border-emerald-200' : warning ? 'bg-amber-50 border-amber-200' : 'bg-white border-brand-border/50'}`}>
            <div className="flex items-center gap-2 mb-1.5">
                <Icon className={`w-4 h-4 ${accent ? 'text-emerald-600' : warning ? 'text-amber-600' : 'text-brand-text-muted'}`} />
                <span className="text-xs text-brand-text-muted">{label}</span>
            </div>
            <div className={`text-base font-bold ${accent ? 'text-emerald-700' : warning ? 'text-amber-700' : 'text-slate-900'}`}>
                {value}
            </div>
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div className="flex justify-between items-start gap-3">
            <span className="text-xs text-brand-text-muted flex-shrink-0">{label}</span>
            <span className="text-sm font-medium text-right break-words">{value || '-'}</span>
        </div>
    );
}

function StatusControl({ label, value, options, getLabel, onChange }) {
    return (
        <div>
            <Label className="text-xs text-brand-text-muted mb-1.5 block">{label}</Label>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger className="h-10 bg-white">
                    <SelectValue>{getLabel(value)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                    {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
            </Select>
        </div>
    );
}

async function updateProjectStatus(projectId, patch, onReload) {
    try {
        await crmAPI.updateProject(projectId, patch);
        await onReload();
    } catch (e) { toast.error('Gagal update status'); }
}

function DocUploadRow({ label, url, kind, projectId, onReload }) {
    const [uploading, setUploading] = useState(false);
    const inputId = `doc-upload-${kind}-${projectId}`;

    const upload = async (file) => {
        try {
            setUploading(true);
            const fd = new FormData();
            fd.append('file', file);
            const up = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            const fileUrl = up.data?.url || up.data?.file_url || up.data?.path || up.data?.fileUrl;
            if (!fileUrl) throw new Error('Upload gagal');
            const update = {}; update[`${kind}_file`] = fileUrl;
            await crmAPI.updateProject(projectId, update);
            toast.success(`${label} berhasil diunggah`);
            await onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message || 'Gagal upload');
        } finally { setUploading(false); }
    };

    return (
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-brand-border/40">
            <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${url ? 'bg-emerald-50' : 'bg-slate-100'}`}>
                    <FileText className={`w-4 h-4 ${url ? 'text-emerald-600' : 'text-slate-400'}`} />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-medium">{label}</div>
                    {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-primary hover:underline truncate block">
                            Lihat file
                        </a>
                    ) : (
                        <div className="text-xs text-brand-text-muted">Belum diupload</div>
                    )}
                </div>
            </div>
            <label htmlFor={inputId} className="cursor-pointer flex-shrink-0 ml-2">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${uploading ? 'bg-slate-100' : 'bg-brand-accent-soft hover:bg-brand-accent-soft/80'}`}>
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin text-brand-primary" /> : <Upload className="w-4 h-4 text-brand-primary" />}
                </div>
            </label>
            <input id={inputId} type="file" className="hidden" onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload(f);
                e.target.value = '';
            }} />
        </div>
    );
}

// =========================== PAYMENT SECTION (UI/UX Improved) ===========================

function PaymentSection({ project, onReload }) {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);

    // Form state
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [invoice, setInvoice] = useState('');
    const [nominal, setNominal] = useState(0);
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState('__none__');
    const [invoiceFile, setInvoiceFile] = useState('');
    const [uploadingFile, setUploadingFile] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadPayments = useCallback(async () => {
        try {
            setLoading(true);
            const res = await crmAPI.listPayments(project.id);
            setPayments(res.data || []);
        } catch (e) { toast.error('Gagal memuat payments'); }
        finally { setLoading(false); }
    }, [project.id]);

    useEffect(() => { loadPayments(); }, [loadPayments]);

    const uploadInvoiceFile = async (file) => {
        try {
            setUploadingFile(true);
            const fd = new FormData();
            fd.append('file', file);
            const up = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            const url = up.data?.url || up.data?.file_url || up.data?.path || up.data?.fileUrl;
            if (!url) throw new Error('Upload gagal');
            setInvoiceFile(url);
            toast.success('File Invoice diunggah');
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message || 'Gagal upload');
        } finally { setUploadingFile(false); }
    };

    const addPayment = async () => {
        if (!invoice.trim()) return toast.error('Nomor Invoice wajib');
        if (!nominal || nominal <= 0) return toast.error('Nominal wajib > 0');
        if (!status || status === '__none__') return toast.error('Payment Status wajib dipilih');
        try {
            setSaving(true);
            await crmAPI.addPayment(project.id, {
                project_id: project.id,
                workspace_id: project.workspace_id,
                date, invoice, nominal, notes,
                status: status === '__none__' ? undefined : status,
                invoice_file: invoiceFile || undefined,
            });
            setInvoice(''); setNominal(0); setNotes(''); setStatus('__none__'); setInvoiceFile('');
            toast.success('Payment ditambahkan');
            await loadPayments();
            await onReload();
            setShowForm(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal');
        } finally { setSaving(false); }
    };

    const removePayment = async (pid) => {
        if (!window.confirm('Hapus payment ini?')) return;
        try { await crmAPI.deletePayment(pid); toast.success('Dihapus'); await loadPayments(); await onReload(); }
        catch (e) { toast.error('Gagal'); }
    };

    const paymentOptions = buildPaymentOptions(project.terms_of_payment);
    const totalPaid = payments.reduce((s, pm) => s + (pm.nominal || 0), 0);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <SectionTitle icon={Receipt} title="Riwayat Payment" />
                <Button size="sm" variant="outline" onClick={() => setShowForm(!showForm)} className="h-8 text-xs">
                    {showForm ? 'Tutup Form' : <><Plus className="w-3 h-3 mr-1" /> Tambah Payment</>}
                </Button>
            </div>

            {/* Payment Form - Progressive Disclosure */}
            {showForm && (
                <div className="bg-white rounded-xl p-5 border border-brand-border/60 shadow-sm space-y-4">
                    <div className="text-sm font-semibold text-slate-800 mb-1">Input Pembayaran Baru</div>

                    {/* Row 1: Date, Invoice #, Status */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <Label className="text-xs font-medium text-brand-text-muted mb-1.5 block">Tanggal *</Label>
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10" />
                        </div>
                        <div>
                            <Label className="text-xs font-medium text-brand-text-muted mb-1.5 block">Invoice # *</Label>
                            <Input value={invoice} onChange={(e) => setInvoice(e.target.value)} placeholder="INV-2024-001" className="h-10" />
                        </div>
                        <div>
                            <Label className="text-xs font-medium text-brand-text-muted mb-1.5 block">Payment Status *</Label>
                            <Select value={status === '__none__' ? undefined : status} onValueChange={setStatus}>
                                <SelectTrigger className="h-10"><SelectValue placeholder="Pilih status..." /></SelectTrigger>
                                <SelectContent>
                                    {paymentOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Row 2: Nominal, Notes */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-medium text-brand-text-muted mb-1.5 block">Nominal *</Label>
                            <Input
                                type="text"
                                value={nominal ? 'IDR ' + Number(nominal).toLocaleString('id-ID') : ''}
                                onChange={(e) => { const raw = e.target.value.replace(/\D/g, ''); setNominal(raw === '' ? 0 : parseInt(raw, 10)); }}
                                placeholder="IDR 0"
                                className="h-10 font-medium"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-medium text-brand-text-muted mb-1.5 block">Keterangan</Label>
                            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opsional" className="h-10" />
                        </div>
                    </div>

                    {/* Row 3: Invoice File + Submit */}
                    <div className="flex items-end gap-4 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                            <Label className="text-xs font-medium text-brand-text-muted mb-1.5 block">Invoice File</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="file"
                                    className="h-10 text-xs py-2"
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadInvoiceFile(f); e.target.value = ''; }}
                                    disabled={uploadingFile}
                                />
                                {invoiceFile && (
                                    <a href={invoiceFile} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-primary hover:underline whitespace-nowrap flex-shrink-0">
                                        Lihat file
                                    </a>
                                )}
                            </div>
                        </div>
                        <Button onClick={addPayment} disabled={saving || uploadingFile} className="h-10 px-6">
                            {saving || uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" /> Simpan Payment</>}
                        </Button>
                    </div>
                </div>
            )}

            {/* Payment List */}
            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>
            ) : payments.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-brand-border/50">
                    <Receipt className="w-8 h-8 mx-auto mb-2 text-brand-text-muted opacity-40" />
                    <div className="text-sm text-brand-text-muted">Belum ada pembayaran tercatat</div>
                </div>
            ) : (
                <div className="space-y-3">
                    {payments.map((pm) => (
                        <div key={pm.id} className="group bg-white rounded-xl p-4 border border-brand-border/50 hover:border-brand-border transition-colors">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0 space-y-2">
                                    {/* Top row: Date, Invoice, Status */}
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Badge variant="outline" className="text-xs font-normal bg-slate-50">{pm.date}</Badge>
                                        <span className="text-sm font-semibold text-slate-900">{pm.invoice}</span>
                                        {pm.status && (
                                            <Badge variant="secondary" className="text-xs">
                                                {getPaymentLabel(pm.status, project.terms_of_payment)}
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Middle: Nominal */}
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-lg font-bold text-emerald-600">{formatIDR(pm.nominal)}</span>
                                        {pm.created_by_name && (
                                            <span className="text-xs text-brand-text-muted">oleh {pm.created_by_name}</span>
                                        )}
                                    </div>

                                    {/* Bottom: Notes + File */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                        {pm.notes && <span className="text-xs text-brand-text-muted">{pm.notes}</span>}
                                        {pm.invoice_file && (
                                            <a href={pm.invoice_file} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-primary hover:underline inline-flex items-center gap-1">
                                                <FileText className="w-3 h-3" /> Invoice File
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removePayment(pm.id)}
                                    className="flex-shrink-0 w-9 h-9 opacity-0 group-hover:opacity-100 transition-opacity"
                                    aria-label="Hapus payment"
                                >
                                    <X className="w-4 h-4 text-red-500" />
                                </Button>
                            </div>
                        </div>
                    ))}

                    {/* Total Footer */}
                    <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-xl border border-brand-border/40">
                        <span className="text-sm text-brand-text-muted">{payments.length} pembayaran</span>
                        <div className="flex items-baseline gap-2">
                            <span className="text-sm text-brand-text-muted">Total terbayar:</span>
                            <span className="text-lg font-bold text-emerald-600">{formatIDR(totalPaid)}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
